import { AddressItemType, CustomerAddress, GQL_ID } from '@@types'
import { useEffect, useRef } from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useCartInfoStore, useCartStore } from '@entities/Cart'
import {
  useLazyInitializeOrder,
  useMutatePlaceOrder,
  useMutateUpdateOrderInfo,
} from '@entities/Checkout'


import {
  useAuth,
  useLoginModal,
  useProfileCustomerModal,
} from '@features/auth'
import { useErrorInfoModal, useModalStore } from '@features/modal'
import { DeliveryForm, PickupForm, UserInfoForm } from '@features/order-form'
import { OrderFormValues } from '@features/order-form/model/shema'
import { useOrderForm } from '@features/order-form/model/useOrderForm.ts'
import DetailOrderForm from '@features/order-form/ui/DetailOrderForm/DetailOrderForm'
import { useAddPayment } from '@features/payment/hooks/useAddPayment'
import { useManagePaymentMethods } from '@features/payment/hooks/useManagePaymentMethods'
import { useAddressStore, useDelivery } from '@features/shipping-methods'



import { useHeaderStore } from '@widgets/header/model/headerStore'
import useDeliveryModal from '@widgets/modal-delivery/ui/useDeliveryModal'
import OrderAmount from '@widgets/order-amount'
import { useSlots } from '@widgets/slots'

import { useLocalizedNavigate as useNavigate } from '@shared/lib/locale'


import { useAnalytics } from '@shared/lib/analytics'
import Button from '@shared/ui/button'
import Container from '@shared/ui/container'
import { Form } from '@shared/ui/form'
import Wrapper from '@shared/ui/wrapper'
import { formatGramsToHumanWeight } from '@shared/uttils/formatGramsToHumanWeight'
import { formatPhoneNumber } from '@shared/uttils/formatPhoneNumber'
import { splitFullName } from '@shared/uttils/splitFullName'
import { useDebounce } from '@shared/uttils/useDebounce'

import SuccessModal from './modal/SuccessModal'
import WarningModal from './modal/WarningModal'

import styles from './CheckoutFlow.module.scss'

export default function CheckoutFlow () {
  const { setVariant } = useHeaderStore()
  const { user, fetchPingUser, requireRegistration } = useAuth()
  const { openProfileCustomerModal } = useProfileCustomerModal()
  const { openLoginModal } = useLoginModal()
  const { selectedAddress, isPickup, shopId, zoneId } = useAddressStore()
  const navigate = useNavigate()
  const { openDeliveryModal } = useDeliveryModal()
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()

  const cartInfo = useCartInfoStore()
  const { clearCart } = useCartStore()

  const { beginCheckout, addShippingInfo, purchase } = useAnalytics()
  const checkoutTrackedRef = useRef(false)
  const shippingTrackedRef = useRef<GQL_ID | null>(null)

  const [ loadOrder, { data: initializeOrderData, loading: orderLoading } ] =
    useLazyInitializeOrder()
  const initializeOrder = initializeOrderData?.initializeOrder

  useEffect(() => {
    async function checkModals () {
      const data = await fetchPingUser()
      const auth = data.data?.pingAuth
      const isRequireRegistration = auth?.requireRegistration

      if (!auth) {
        openLoginModal(() => {
          navigate('/cart')
        })
        return
      }

      if (isRequireRegistration) {
        openProfileCustomerModal(() => {
          navigate('/cart')
        })
        return
      }

      if (!selectedAddress) {
        openDeliveryModal(() => {
          navigate('/cart')
        })
        return
      }

      // Если slot-доставка, но zoneId не установлен — нужно выбрать/обновить адрес
      if (!isPickup() && !zoneId) {
        openDeliveryModal(() => {
          navigate('/cart')
        })
        return
      }
    }

    checkModals()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const canLoad = user && !requireRegistration && !initializeOrder && shopId

    if (!canLoad) return

    // Синхронизируем корзину перед созданием заказа БЕЗ isSoldOut товаров
    const initOrder = async () => {
      await useCartStore.getState().syncCart(undefined, false)
      loadOrder({
        variables: {
          shop_id: shopId,
        },
      })
    }

    initOrder()
  }, [ user, selectedAddress, initializeOrder, requireRegistration, shopId, loadOrder ])

  const methods = useOrderForm() as UseFormReturn<OrderFormValues>
  const { t: o } = useTranslation('order')
  const { t: tc } = useTranslation('common')
  const [ updateOrderInfo ] = useMutateUpdateOrderInfo()
  const [ placeOrder, { data: placeOrderData, loading: orderPlaceLoading } ] =
    useMutatePlaceOrder()
  const { openModal } = useModalStore()
  const { openSlotsModal } = useSlots()
  const { selectedSlot, maxSlotWeight, maxExpressWeight, deliveryType, deliveryCost } = useDelivery()
  const watched = useWatch<OrderFormValues>({
    control: methods.control,
  })
  const { addPayment } = useAddPayment()
  const { paymentMethods } = useManagePaymentMethods()

  useEffect(() => {
    setVariant('minimal')
    return () => setVariant('default')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Track begin_checkout when checkout is initialized
  useEffect(() => {
    const products = cartInfo.cartInfo?.products
    if (initializeOrder && products && products.length > 0 && !checkoutTrackedRef.current) {
      checkoutTrackedRef.current = true
      beginCheckout(products)
    }
  }, [ initializeOrder, cartInfo.cartInfo?.products ])

  // Track add_shipping_info when slot is selected
  useEffect(() => {
    const products = cartInfo.cartInfo?.products
    if (selectedSlot?.id && products && products.length > 0 && shippingTrackedRef.current !== selectedSlot.id) {
      shippingTrackedRef.current = selectedSlot.id
      const shippingTier = isPickup() ? 'pickup' : (deliveryType || 'slot')
      addShippingInfo(products, shippingTier)
    }
  }, [ selectedSlot?.id, cartInfo.cartInfo?.products, deliveryType ])

  useEffect(() => {
    const placedOrder = placeOrderData?.placeOrder
    if (!placedOrder) return

    const { error_code, order_id } = placedOrder
    if (typeof order_id !== 'string') return

    if (error_code === 0) {
      // Track purchase
      const products = cartInfo.cartInfo?.products || []
      purchase(
        order_id, 
        products, 
        {
          shipping: deliveryCost
        }
      )

      openModal(
        'successPaymentOrder',
        <SuccessModal selectSlot={selectedSlot} id={order_id} />,
      )
      clearCart()
    }
    else if ([ 1, 2, 3 ].includes(error_code)) {
      openModal('warningPaymentOrder', <WarningModal />, {
        title: o('error'),
        description: o('error_description'),
        onClose: () => location.reload(),
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ placeOrderData?.placeOrder ])

  useEffect(() => {
    if (!user) return
    methods.reset({
      name: user.first_name || '',
      phone: formatPhoneNumber(user.phone || ''),
      address: selectedAddress?.address || '',
      replacementOption: initializeOrder?.assembly_method ?? 1,
      pickup: isPickup(),
    })
  }, [ user, initializeOrder, selectedAddress, methods, isPickup ])

  const createOrder = async () => {
    try {
      await placeOrder({
        variables: {
          order_id: initializeOrder?.id as string,
        },
        update: (cache, { data }) => {
          const newOrder = data?.placeOrder
          if (!newOrder) return

          cache.modify({
            fields: {
              allMyOrders (existing = { edges: [] }) {
                return {
                  ...existing,
                  edges: [ newOrder, ...existing.edges ],
                }
              },
            },
          })
        },
      })
    }
    catch (e) {
      console.error('Ошибка GraphQL или сети:', e)
      openErrorInfoModal({
        description: te('create_order'),
      })
    }
  }

  const onSubmit = async (selected: GQL_ID) => {
    if (selected === 'new-card') {
      // Оплата новой картой через GPB
      const result = await addPayment('gpb', initializeOrder?.id as string)
      
      // Универсальная обработка на основе type
      if (result?.type === 'redirect') {
        // Редирект уже произошел в useAddPayment
        return
      }
      else if (result?.type === 'qr') {
        // QR код - показываем модалку
        const { SBPPayment } = await import('@features/payment')
        openModal(
          'paymentQR',
          <SBPPayment
            qrUrl={result.qrUrl!}
            operationId={String(result.operationId)}
            verificationUrl={result.verificationUrl!}
          />,
          { title: o('pay_by_card') },
        )
      }
      else if (result?.type === 'controllable' || result?.type === 'embed') {
        // Кастомизированный компонент или embed - пока не реализовано
        console.warn('Payment type not implemented:', result?.type)
      }
    }
    else if (selected === 'new-sbp') {
      // Оплата через СБП
      const result = await addPayment('gpb_sbp', initializeOrder?.id as string)
      
      // Универсальная обработка на основе type
      if (result?.type === 'qr') {
        // Показываем СБП QR
        const { SBPPayment } = await import('@features/payment')
        openModal(
          'sbpPayment',
          <SBPPayment
            qrUrl={result.qrUrl!}
            operationId={String(result.operationId)}
            verificationUrl={result.verificationUrl!}
          />,
          { title: o('pay_by_sbp') },
        )
      }
      else if (result?.type === 'redirect') {
        // Редирект уже произошел в useAddPayment
        return
      }
      else if (result?.type === 'controllable' || result?.type === 'embed') {
        // Кастомизированный компонент или embed - пока не реализовано
        console.warn('Payment type not implemented:', result?.type)
      }
    }
    else {
      // Оплата существующим платежным методом (карта или СБП)
      try {
        const selectedMethod = paymentMethods.find(m => m?.id === selected)
        if (!selectedMethod) {
          openErrorInfoModal({
            description: te('payment_method_not_found'),
          })
          return
        }

        // Обновляем заказ с привязкой платежного метода
        await updateOrderInfo({
          variables: {
            order_id: initializeOrder?.id as string,
            orderInfo: {
              paymentToken: selectedMethod.id,
              payment_type: selectedMethod.pluginId,
            },
          },
        })

        // Размещаем заказ
        await createOrder()
      }
      catch (e) {
        console.error('Ошибка при привязке платежного метода:', e)
        openErrorInfoModal({
          description: te('bind_payment_method'),
        })
      }
    }
  }

  const {
    comment,
    noIntercomCall,
    replacementOption,
    nameOther,
    phoneOther,
    leaveAtDoor,
    commentBuilder,
  } = watched

  const debouncedName = useDebounce(nameOther, 2000)
  const debouncedPhone = useDebounce(phoneOther, 2000)
  const debouncedComment = useDebounce(comment, 2000)
  const debouncedCommentBuilder = useDebounce(commentBuilder, 2000)

  const weightLimit = deliveryType === 'express' ? maxExpressWeight : maxSlotWeight

  const isWeightExceeded = () => {
    // Если экспресс — используем maxExpressWeight, иначе slot
    const limit = weightLimit
    if (!limit || limit <= 0) return false

    // totalWeight находится в cartInfoStore → для простоты запросим из cart store
    const totalWeight = cartInfo.cartInfo?.totalWeight ?? 0

    return totalWeight > limit
  }

  const weightExceeded = isWeightExceeded()

  useEffect(() => {
    const { firstName, lastName } = splitFullName(debouncedName || '')
    const isCustomer = (
      addr: AddressItemType | null,
    ): addr is CustomerAddress =>
      !!addr && typeof addr === 'object' && 'shop' in addr

    const geo = {
      latitude:
        selectedAddress?.customerGeoPoint?.latitude ??
        (isCustomer(selectedAddress)
          ? selectedAddress.shop?.shopGeoPoint?.latitude
          : undefined),
      longitude:
        selectedAddress?.customerGeoPoint?.longitude ??
        (isCustomer(selectedAddress)
          ? selectedAddress.shop?.shopGeoPoint?.longitude
          : undefined),
    }

    if (
      geo.latitude &&
      geo.longitude &&
      selectedSlot?.id &&
      initializeOrder?.id
    ) {
      updateOrderInfo({
        variables: {
          order_id: initializeOrder.id,
          orderInfo: {
            zone_id: zoneId ? (typeof zoneId === 'number' ? zoneId : parseInt(zoneId)) : null,
            address: selectedAddress?.address || '',
            address_appartments: selectedAddress?.address_appartments,
            address_intercom: selectedAddress?.address_intercom,
            address_floor: selectedAddress?.address_floor,
            address_porch: selectedAddress?.address_porch,
            comment: debouncedCommentBuilder,
            no_door_call: noIntercomCall,
            door_delivery: leaveAtDoor,
            assembly_method: replacementOption,
            customer_geoposition: {
              latitude:
                selectedAddress?.customerGeoPoint?.latitude ??
                (isCustomer(selectedAddress)
                  ? selectedAddress.shop?.shopGeoPoint?.latitude
                  : undefined),
              longitude:
                selectedAddress?.customerGeoPoint?.longitude ??
                (isCustomer(selectedAddress)
                  ? selectedAddress.shop?.shopGeoPoint?.longitude
                  : undefined),
            },
            slot_id: +selectedSlot?.id,
            customerCourierMessage: debouncedComment,
            recipientInfo: {
              firstName,
              lastName,
              phone: phoneOther,
            },
          },
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    noIntercomCall,
    replacementOption,
    debouncedName,
    debouncedPhone,
    leaveAtDoor,
    debouncedComment,
    debouncedCommentBuilder,
    selectedSlot?.id,
    initializeOrder?.id,
    selectedAddress,
    zoneId
  ])

  return (
    <Container>
      <div className={styles.root}>
        <div className={styles.root__left}>
          <div className={styles.wrapper}>
            <Form {...methods}>
              <form className={styles.root__form}>
                {isPickup() ? <PickupForm /> : <DeliveryForm />}
                <UserInfoForm isPickup={isPickup()} />
                {selectedSlot && (
                  <Wrapper className={styles.banner}>
                    <h3>{o('data_time')}</h3>
                    <div className={styles.time}>
                      <span>
                        {selectedSlot.slotDate} {tc('from')}{' '}
                        {selectedSlot.slotTime}
                      </span>
                      <Button
                        className={styles.time__button}
                        theme="quaternary"
                        onClick={() => {
                          openSlotsModal()
                        }}
                      >
                        {tc('change')}
                      </Button>
                    </div>
                  </Wrapper>
                )}
                <DetailOrderForm />
              </form>
            </Form>

            {isPickup() && (
              <Wrapper className={styles.banner}>
                <h3>{o('banner.title')}</h3>
                <p>{o('banner.description')}</p>
              </Wrapper>
            )}
          </div>
        </div>

        <OrderAmount
          loading={
            orderPlaceLoading || orderLoading
          }
          disabled={weightExceeded}
          buttonText={weightExceeded && weightLimit
            ? o('weight_exceeded', {
              maxWeight: formatGramsToHumanWeight({ weight: weightLimit, t: tc }),
            })
            : undefined}
          onPaymentMethodChange={async (paymentId, pluginId) => {
            if (initializeOrder?.id) {
              await updateOrderInfo({
                variables: {
                  order_id: initializeOrder.id,
                  orderInfo: {
                    paymentToken: paymentId,
                    payment_type: pluginId,
                  },
                },
              })
            }
          }}
          onSubmit={onSubmit}
        />
      </div>
    </Container>
  )
}
